import React, { useEffect, useState, useCallback, useRef } from "react";
// Removed unused 'axios' and 'backendApiUrl'
import { useAuth } from "../context/AuthContext";
import styles from "../views/Quiz.module.css"; // Reuse original styles
import LoadingSpinner from "../components/LoadingSpinner";
import useJourneyLogic from "../hooks/useJourneyLogic";
// Removed unused 'commonGameStyles'

function QuizJourneyView({ onComplete, gameLength = 2 }) {
    const { user } = useAuth();
    const autoAdvanceTimeoutRef = useRef(null);

    // --- 1. STATE & HOOKS (MUST BE UNCONDITIONAL) ---

    const [selected, setSelected] = useState(null);
    const [showExplanation, setShowExplanation] = useState(false);
    const [done, setDone] = useState(false); // Local state to trigger end
    const [timer, setTimer] = useState(30);
    const [showInstructions, setShowInstructions] = useState(true);
    const [isCorrect, setIsCorrect] = useState(false);
    const [localScore, setLocalScore] = useState(0);

    const ACTIVITY_TYPE = "originalQuiz";

    // Custom Hook call must be at the top
    const {
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
        isGameEnded,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    } = useJourneyLogic({
        activityType: ACTIVITY_TYPE,
        gameSpecificEndCondition: done,
        gameLength,
        onComplete,
    });

    // --- 2. CALLBACKS (MUST BE UNCONDITIONAL) ---

    // --- Start quiz ---
    const startQuiz = useCallback(() => {
        setShowInstructions(false);
        setTimer(30);
    }, []); // ❌ FIX: Moved to the top

    // --- Next question ---
    const next = useCallback(() => {
        setSelected(null);
        setShowExplanation(false);
        setTimer(30);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent((prev) => prev + 1);
        } else {
            // Set local 'done' state, which triggers the hook's completion logic
            setDone(true);
        }
    }, [current, questions.length, setCurrent]); // ❌ FIX: Moved to the top

    // --- Timeout logic ---
    const handleTimeout = useCallback(() => {
        setSelected(null);
        setShowExplanation(true);
        setIsCorrect(false);
        // Call simplified incorrect handler (no lives removed)
        handleIncorrectAttempt(questions[current]?._id);
    }, [handleIncorrectAttempt, questions, current]); // ❌ FIX: Moved to the top

    // --- Select option ---
    const handleSelect = useCallback(
        (choiceIndex) => {
            setSelected(choiceIndex);
        },
        []
    ); // ❌ FIX: Moved to the top

    // --- Submit answer ---
    const handleSubmit = useCallback(async () => {
        if (selected === null) return;
        setShowExplanation(true);

        const currentQ = questions[current];
        const selectedOptionText = currentQ.options[selected];
        const isCorrectAnswer = selectedOptionText === currentQ.correctAnswerText;
        setIsCorrect(isCorrectAnswer);

        if (isCorrectAnswer) {
            setLocalScore((prev) => prev + 1);
            handleCorrectAttempt(currentQ?._id);
        } else {
            handleIncorrectAttempt(currentQ?._id);
        }

    }, [
        selected,
        current,
        questions,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    ]); // ❌ FIX: Moved to the top

    // --- 3. EFFECTS (MUST BE UNCONDITIONAL) ---

    // --- Load questions ---
    useEffect(() => {
        if (!user) return;
        loadQuizQuestions(null, true);
    }, [user, loadQuizQuestions]); // ❌ FIX: Moved to the top


    // --- Timer logic ---
    useEffect(() => {
        // Logic inside is conditional, which is allowed.
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions
        )
            return;
        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [
        current,
        showExplanation,
        done,
        loadingQuestions,
        questions.length,
        showInstructions,
        handleTimeout,
    ]); // ❌ FIX: Moved to the top

    // --- Auto advance after showing explanation ---
    useEffect(() => {
        // Logic inside is conditional, which is allowed.
        if (!showExplanation) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            next();
        }, 8000);
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, next]); // ❌ FIX: Moved to the top


    // --- 4. CONDITIONAL RENDERING (After all hooks) ---

    // CRITICAL: If the game is ended (by the hook), return null immediately.
    if (isGameEnded) {
        return null;
    }

    // --- Styling helper for options ---
    const getOptionClass = (choiceIndex) => {
        if (!questions || questions.length === 0 || current >= questions.length) {
            return styles.optionButton;
        }

        const currentQ = questions[current];
        const optionText = currentQ.options[choiceIndex];
        const isSelected = choiceIndex === selected;

        if (showExplanation) {
            const isCorrectOption = optionText === currentQ.correctAnswerText;
            if (isCorrectOption) return `${styles.optionButton} ${styles.correct}`;
            if (isSelected) return `${styles.optionButton} ${styles.incorrect}`;
            return styles.optionButton;
        }

        return isSelected
            ? `${styles.optionButton} ${styles.selected}`
            : styles.optionButton;
    };


    // --- Early returns (Simplified) ---
    const backgroundStyle = { backgroundImage: `url("/images/qiblah.jpg")` };

    if (!user)
        return <p className={styles.message}>Please login to access the activity.</p>;

    // Simplified Loading/Error returns
    if (loadingQuestions)
        return <div className={styles.background} style={backgroundStyle}><div className={styles.overlay}></div><div className={styles.quizContainer}><LoadingSpinner /></div></div>;

    // 🟢 FIX: Corrected syntax from `<div className.quizContainer}` to `<div className={styles.quizContainer}>`
    if (error)
        return <div className={styles.background} style={backgroundStyle}><div className={styles.overlay}></div><div className={styles.quizContainer}><p className={styles.errorMessage}>Error: {error}</p></div></div>;
    if (questions.length === 0 && !loadingQuestions)
        return <div className={styles.background} style={backgroundStyle}><div className={styles.overlay}></div><div className={styles.quizContainer}><p className={styles.message}>No questions found.</p></div></div>;


    const currentQ = questions[current];
    const progressPercent = ((current + 1) / questions.length) * 100;

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.quizContainer} style={{ position: "relative" }}>
                <h2 className={styles.title}>🧠 Journey Activity: Quiz</h2>

                {/* --- Instructions (Simplified for Journey) --- */}
                {showInstructions ? (
                    <div className={styles.instructionsPopup}>
                        <h3>Journey Mode Instructions:</h3>
                        <ul>
                            <li>Complete the mini-activity to advance in your journey.</li>
                            <li>Your goal is completion, not perfection!</li>
                        </ul>
                        <button className={styles.proceedButton} onClick={startQuiz}>
                            Start Activity
                        </button>
                    </div>
                ) : (
                    // --- Main Quiz UI ---
                    <>
                        <p className={styles.progressText}>
                            Question {current + 1} of {questions.length} (Score: {localScore})
                        </p>
                        <div className={styles.progressWrapper}>
                            <div
                                className={styles.progressBar}
                                style={{ width: `${progressPercent}%` }}
                            ></div>
                        </div>

                        <div className={styles.timer}>⏱ {timer}s</div>
                        <h3 className={styles.question}>{currentQ.question}</h3>

                        <ul className={styles.options}>
                            {currentQ.options.map((option, index) => (
                                <li key={index}>
                                    <button
                                        className={getOptionClass(index)}
                                        onClick={() => handleSelect(index)}
                                        disabled={showExplanation}
                                    >
                                        {option}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {!showExplanation && (
                            <button
                                className={styles.submit}
                                onClick={handleSubmit}
                                disabled={selected === null}
                            >
                                Submit
                            </button>
                        )}

                        {/* --- Explanation --- */}
                        {showExplanation && (
                            <div className={styles.explanationPopup}>
                                <p className={isCorrect ? styles.correctText : styles.incorrectText}>
                                    {isCorrect ? "✅ Correct, MashaAllah!" : "❌ Incorrect."}
                                </p>
                                <p>
                                    <strong>Explanation:</strong> {currentQ.explanation}
                                </p>
                                <p className={styles.reference}>
                                    <strong>Reference:</strong> {currentQ.reference}
                                </p>
                                <button onClick={next} className={styles.nextButton}>
                                    {current + 1 < questions.length
                                        ? "Next Question"
                                        : "Complete Activity"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default QuizJourneyView;
